import styled from 'styled-components';
import { Spinner } from 'ui/spinner';
import { ListsEmptyState } from 'ui/illustrations';
import { EmptyState } from 'ui/emptyState';
import { GridRow } from 'ui/indexPages/tables/grid';
import { spacing } from 'ui/theme';
import { Link } from 'ui/links';
import { useMembersContext } from 'modules/roles/contexts/MembersContext';
import { gridLayout } from 'modules/roles/constants';
import { usePermissionsContext, useUsersQueryContext } from 'modules/roles/contexts';
import { CombinedRole } from 'modules/roles/types';
import { PermissionsTableCell } from './PermissionsTableCell';
import { MembersTableCell } from './MembersTableCell';
import { LastModifiedTableCell } from './LastModifiedTableCell';

const StyledGridRow = styled(GridRow)`
  align-items: center;
  gap: ${spacing.base};
  padding-right: ${spacing.base};
  padding-left: ${spacing.base};
`;

const Container = styled.div`
  width: 100%;
  min-height: 40vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: ${spacing.x3};
  padding-bottom: ${spacing.x3};
`;

export const TABLE_BODY_TEXT = {
  emptyState: {
    id: 'roles-table-empty-state',
    title: 'No roles to display',
    subtitle: 'Get started by adding a role.',
    noMembers: 'No members',
  },
};

type TableBodyProps = {
  isLoading: boolean;
  roles: CombinedRole[];
};

export const TableBody = ({ isLoading, roles }: TableBodyProps) => {
  const { getMemberByUserId } = useMembersContext();
  const { getPermissionById } = usePermissionsContext();
  const { getUserById } = useUsersQueryContext();

  if (isLoading) {
    return (
      <Container data-testid="roles-table-loading">
        <Spinner />
      </Container>
    );
  }

  if (roles.length === 0) {
    return (
      <Container data-testid={TABLE_BODY_TEXT.emptyState.id}>
        <EmptyState
          title={TABLE_BODY_TEXT.emptyState.title}
          subtitle={TABLE_BODY_TEXT.emptyState.subtitle}
          Illustration={ListsEmptyState}
        />
      </Container>
    );
  }

  return (
    <>
      {roles.map((role) => {
        return (
          <StyledGridRow gridLayout={gridLayout} key={role.id}>
            <Link inTable useReactRouter href={`/roles/${role.id}`}>
              {role.name}
            </Link>
            <PermissionsTableCell
              getPermissionById={getPermissionById}
              permissionsList={role.permissions}
            />
            <MembersTableCell
              getMemberByUserId={getMemberByUserId}
              getUserById={getUserById}
              members={role.members}
            />
            <LastModifiedTableCell
              getMemberByUserId={getMemberByUserId}
              getUserById={getUserById}
              role={role}
            />
          </StyledGridRow>
        );
      })}
    </>
  );
};
